import type { AuthUser, LoginResponse, ProblemDetails } from '@runik/shared';

let csrfToken: string | null = null;
const csrfListeners = new Set<() => void>();
const unauthorizedListeners = new Set<() => void>();

export class ApiError extends Error {
  constructor(public problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
  }
}

export function setCsrfToken(t: string | null): void {
  csrfToken = t;
  csrfListeners.forEach((fn) => fn());
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function subscribeCsrf(fn: () => void): () => void {
  csrfListeners.add(fn);
  return () => csrfListeners.delete(fn);
}

export function onUnauthorized(fn: () => void): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

const STATE_CHANGING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function request<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (STATE_CHANGING.has(method) && csrfToken) headers.set('X-CSRF-Token', csrfToken);

  const res = await fetch(path, { ...init, method, headers, credentials: 'include' });
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  const body: unknown = contentType.includes('json') ? await res.json() : await res.text();

  if (!res.ok) {
    if (res.status === 401 && !path.endsWith('/api/auth/me')) {
      unauthorizedListeners.forEach((fn) => fn());
    }
    if (typeof body === 'object' && body && 'status' in body) {
      throw new ApiError(body as ProblemDetails);
    }
    throw new ApiError({ type: 'about:blank', title: res.statusText, status: res.status });
  }
  return body as T;
}

export const auth = {
  async issueCsrf(): Promise<string> {
    const r = await request<{ csrfToken: string }>('/api/auth/csrf');
    setCsrfToken(r.csrfToken);
    return r.csrfToken;
  },
  login(email: string, password: string): Promise<LoginResponse> {
    return request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  async logout(): Promise<void> {
    await request('/api/auth/logout', { method: 'POST' });
    setCsrfToken(null);
  },
  async me(): Promise<AuthUser | null> {
    try {
      return await request<AuthUser>('/api/auth/me');
    } catch (err) {
      if (err instanceof ApiError && err.problem.status === 401) return null;
      throw err;
    }
  },
};
